import React, { useState, useMemo, useCallback } from 'react';
import { Settings, Target } from 'lucide-react';
import { useSelectedMetrics, getCosClassName, getCpaClassName, getMetricObjectiveClassName, useComponentMetrics } from '../../utils/metricsUtils';
import MetricsConfigModal from '../MetricsConfigModal';
import ModuleVisibilityToggle from '../ModuleVisibilityToggle';

/**
 * Composant table des groupes d'annonces Microsoft Ads
 * Optimisé avec React.memo et useMemo pour les performances
 */
const AdGroupsTable = React.memo(({
  adGroupsData = [],
  isVisible = true,
  onToggleVisibility,
  formatNumber,
  selectedAccount = null,
  itemsPerPage = 10,
  userRole = 'USER',
  isClientModuleVisible,
  toggleClientModuleVisibility,
  moduleId = 'msads_ad_groups'
}) => {
  // États locaux pour cette table
  const [sortConfig, setSortConfig] = useState({ key: 'cost', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [showAdGroups, setShowAdGroups] = useState(true);
  const [showMetricsConfig, setShowMetricsConfig] = useState(false);

  // Hook pour les métriques sélectionnées (pour les couleurs COS/CPA)
  const selectedMetrics = useSelectedMetrics();

  // Hook pour les métriques du composant (réactif aux changements)
  const componentMetrics = useComponentMetrics('msads_adgroups', selectedAccount?.id || 'global');

  // Fonction helper pour vérifier si une métrique est sélectionnée pour ce composant
  const isComponentMetricActive = useCallback((metricKey) => {
    return componentMetrics.metrics.includes(metricKey);
  }, [componentMetrics.metrics]);

  // Fonction de tri memoized
  const sortedAdGroups = useMemo(() => {
    if (!adGroupsData.length) return [];

    const sorted = [...adGroupsData];
    sorted.sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];
      
      // Cas spécial pour le nom
      if (sortConfig.key === 'name') {
        aValue = String(aValue || '').toLowerCase();
        bValue = String(bValue || '').toLowerCase();
        return sortConfig.direction === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      // Pour les valeurs numériques
      if (typeof aValue === 'string' && !isNaN(aValue)) aValue = parseFloat(aValue);
      if (typeof bValue === 'string' && !isNaN(bValue)) bValue = parseFloat(bValue);
      
      aValue = Number(aValue) || 0;
      bValue = Number(bValue) || 0;
      
      return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
    });
    return sorted;
  }, [adGroupsData, sortConfig]);

  // Pagination des groupes d'annonces (memoized)
  const paginatedAdGroups = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedAdGroups.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedAdGroups, currentPage, itemsPerPage]);

  // Calcul du nombre total de pages
  const totalPages = Math.ceil(sortedAdGroups.length / itemsPerPage);

  // Calcul des totaux memoized
  const totals = useMemo(() => {
    if (!adGroupsData.length) return {};

    const baseTotals = adGroupsData.reduce((acc, adGroup) => ({
      impressions: acc.impressions + (adGroup.impressions || 0),
      clicks: acc.clicks + (adGroup.clicks || 0),
      cost: acc.cost + (adGroup.cost || 0),
      conversions: acc.conversions + (adGroup.conversions || 0),
      conversionsValue: acc.conversionsValue + (adGroup.conversionsValue || 0)
    }), { impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0 });

    // Calculer les métriques dérivées
    return {
      ...baseTotals,
      ctr: baseTotals.impressions > 0 ? (baseTotals.clicks / baseTotals.impressions * 100) : 0,
      cpc: baseTotals.clicks > 0 ? (baseTotals.cost / baseTotals.clicks) : 0,
      conversionRate: baseTotals.clicks > 0 ? (baseTotals.conversions / baseTotals.clicks * 100) : 0,
      cpa: baseTotals.conversions > 0 ? (baseTotals.cost / baseTotals.conversions) : 0,
      cos: baseTotals.conversionsValue > 0 ? (baseTotals.cost / baseTotals.conversionsValue * 100) : 0
    };
  }, [adGroupsData]);

  // Handler de tri memoized
  const handleSort = useCallback((key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
    setCurrentPage(1); // Réinitialiser à la première page lors du tri
  }, []);

  // Ne pas rendre si pas de données
  if (!adGroupsData.length) return null;

  // Vérification de visibilité pour les clients
  const clientModuleVisible = isClientModuleVisible ? isClientModuleVisible(moduleId) : true;
  if (userRole === 'CLIENT' && !clientModuleVisible) {
    return null;
  }

  return (
    <div className="ad-groups-table-section">
      <div className="section-header">
        <h4 style={{ fontSize: '18px', fontWeight: '600' }}>
          <Target size={16} />&nbsp;Groupes d'annonces Microsoft Ads ({sortedAdGroups.length})
        </h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {userRole !== 'CLIENT' && (
            <button
              onClick={() => setShowMetricsConfig(true)}
              className="metrics-settings-btn"
              title="Configurer les métriques"
            >
              <Settings size={16} />
            </button>
          )}
          {toggleClientModuleVisibility ? (
            <ModuleVisibilityToggle
              isVisible={clientModuleVisible}
              onToggle={toggleClientModuleVisibility}
              userRole={userRole}
              moduleId={moduleId}
              title="Groupes d'annonces Microsoft Ads"
            />
          ) : (
            <button
              onClick={() => setShowAdGroups(!showAdGroups)}
              className="section-toggle-btn"
              title={showAdGroups ? "Masquer la section" : "Afficher la section"}
            >
              {showAdGroups ? '−' : '+'}
            </button>
          )}
        </div>
      </div>

      {(userRole !== 'CLIENT' || clientModuleVisible) && showAdGroups && (
        <>
          <div className="ad-groups-table-wrapper">
            <table className="campaigns-table" style={{ width: '100%', tableLayout: 'auto', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th onClick={() => handleSort('name')} className="sortable">
                    Groupe d'annonces {sortConfig.key === 'name' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                  </th>
                  <th onClick={() => handleSort('campaignName')} className="sortable">
                    Campagne {sortConfig.key === 'campaignName' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                  </th>
                  {isComponentMetricActive('impressions') && (
                    <th onClick={() => handleSort('impressions')} className="sortable numeric">
                      Impressions {sortConfig.key === 'impressions' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricActive('ctr') && (
                    <th onClick={() => handleSort('ctr')} className="sortable numeric">
                      CTR {sortConfig.key === 'ctr' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricActive('clics') && (
                    <th onClick={() => handleSort('clicks')} className="sortable numeric">
                      Clics {sortConfig.key === 'clicks' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricActive('cpc') && (
                    <th onClick={() => handleSort('cpc')} className="sortable numeric">
                      CPC {sortConfig.key === 'cpc' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricActive('cout') && (
                    <th onClick={() => handleSort('cost')} className="sortable numeric">
                      Coût {sortConfig.key === 'cost' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricActive('taux_conv') && (
                    <th onClick={() => handleSort('conversionRate')} className="sortable numeric">
                      Taux conv. {sortConfig.key === 'conversionRate' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricActive('conv') && (
                    <th onClick={() => handleSort('conversions')} className="sortable numeric">
                      Conv. {sortConfig.key === 'conversions' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricActive('cpa') && (
                    <th onClick={() => handleSort('cpa')} className="sortable numeric">
                      CPA {sortConfig.key === 'cpa' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricActive('valeur_conv') && (
                    <th onClick={() => handleSort('conversionsValue')} className="sortable numeric">
                      Valeur Conv. {sortConfig.key === 'conversionsValue' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricActive('cos') && (
                    <th onClick={() => handleSort('cos')} className="sortable numeric">
                      COS {sortConfig.key === 'cos' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricActive('roas') && (
                    <th onClick={() => handleSort('roas')} className="sortable numeric">
                      ROAS {sortConfig.key === 'roas' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricActive('roi') && (
                    <th onClick={() => handleSort('roi')} className="sortable numeric">
                      ROI {sortConfig.key === 'roi' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                </tr>
              </thead>

              <tbody>
                {paginatedAdGroups.map((adGroup, index) => (
                  <tr key={adGroup.id || index}>
                    <td className="ad-group-name" style={{ display: 'flex', alignItems: 'center' }}>
                      {adGroup.isDelivering !== undefined && (
                        <span
                          style={{
                            display: 'inline-block',
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: adGroup.isDelivering ? '#22c55e' : '#ef4444',
                            marginRight: '8px',
                            flexShrink: 0
                          }}
                          title={adGroup.isDelivering ? 'Actif' : 'Inactif'}
                        />
                      )}
                      <span>{adGroup.name || 'Sans nom'}</span>
                    </td>
                    <td className="campaign-name">
                      {adGroup.campaignName || adGroup.campaign || 'N/A'}
                    </td>
                    {isComponentMetricActive('impressions') && (
                      <td className="numeric">{formatNumber(adGroup.impressions || 0)}</td>
                    )}
                    {isComponentMetricActive('ctr') && (
                      <td className="numeric">{formatNumber(adGroup.ctr || 0, 2)}%</td>
                    )}
                    {isComponentMetricActive('clics') && (
                      <td className="numeric">{formatNumber(adGroup.clicks || 0)}</td>
                    )}
                    {isComponentMetricActive('cpc') && (
                      <td className="numeric">€{formatNumber(adGroup.cpc || 0, 2)}</td>
                    )}
                    {isComponentMetricActive('cout') && (
                      <td className="numeric">€{formatNumber(adGroup.cost || 0, 2)}</td>
                    )}
                    {isComponentMetricActive('taux_conv') && (
                      <td className="numeric">{formatNumber(adGroup.conversionRate || 0, 2)}%</td>
                    )}
                    {isComponentMetricActive('conv') && (
                      <td className="numeric">{formatNumber(adGroup.conversions || 0, 1)}</td>
                    )}
                    {isComponentMetricActive('cpa') && (
                      <td className={`numeric ${getCpaClassName(adGroup.cpa || 0, selectedAccount?.id || 'global', moduleId)}`}>
                        €{formatNumber(adGroup.cpa || 0, 2)}
                      </td>
                    )}
                    {isComponentMetricActive('valeur_conv') && (
                      <td className="numeric">€{formatNumber(adGroup.conversionsValue || 0, 2)}</td>
                    )}
                    {isComponentMetricActive('cos') && (
                      <td className={`numeric ${getCosClassName(adGroup.cos || 0, selectedAccount?.id || 'global', moduleId)}`}>
                        {formatNumber(adGroup.cos || 0, 1)}%
                      </td>
                    )}
                    {isComponentMetricActive('roas') && (() => {
                      const roas = adGroup.cost > 0 ? (adGroup.conversionsValue / adGroup.cost) * 100 : 0;
                      return (
                        <td className={`numeric ${getMetricObjectiveClassName('roas', roas, moduleId, selectedAccount?.id || 'global')}`}>
                          {roas >= 0 ? `${formatNumber(roas, 0)}%` : '-'}
                        </td>
                      );
                    })()}
                    {isComponentMetricActive('roi') && (() => {
                      const hasData = adGroup.cost > 0 && adGroup.conversionsValue > 0;
                      const roi = hasData ? (adGroup.conversionsValue - adGroup.cost) / adGroup.cost : 0;
                      return (
                        <td className={`numeric ${getMetricObjectiveClassName('roi', roi, moduleId, selectedAccount?.id || 'global')}`}>
                          {formatNumber(roi, 2)}
                        </td>
                      );
                    })()}
                  </tr>
                ))}
              </tbody>

              <tfoot>
                <tr className="total-row">
                  <td className="ad-group-name"><strong>TOTAL</strong></td>
                  <td className="campaign-name"><strong>-</strong></td>
                  {isComponentMetricActive('impressions') && (
                    <td className="numeric"><strong>{formatNumber(totals.impressions)}</strong></td>
                  )}
                  {isComponentMetricActive('ctr') && (
                    <td className="numeric"><strong>{formatNumber(totals.ctr, 2)}%</strong></td>
                  )}
                  {isComponentMetricActive('clics') && (
                    <td className="numeric"><strong>{formatNumber(totals.clicks)}</strong></td>
                  )}
                  {isComponentMetricActive('cpc') && (
                    <td className="numeric"><strong>€{formatNumber(totals.cpc, 2)}</strong></td>
                  )}
                  {isComponentMetricActive('cout') && (
                    <td className="numeric"><strong>€{formatNumber(totals.cost, 2)}</strong></td>
                  )}
                  {isComponentMetricActive('taux_conv') && (
                    <td className="numeric"><strong>{formatNumber(totals.conversionRate, 2)}%</strong></td>
                  )}
                  {isComponentMetricActive('conv') && (
                    <td className="numeric"><strong>{formatNumber(totals.conversions, 1)}</strong></td>
                  )}
                  {isComponentMetricActive('cpa') && (
                    <td className={`numeric ${getCpaClassName(totals.cpa, selectedAccount?.id || 'global', moduleId)}`}>
                      <strong>€{formatNumber(totals.cpa, 2)}</strong>
                    </td>
                  )}
                  {isComponentMetricActive('valeur_conv') && (
                    <td className="numeric"><strong>€{formatNumber(totals.conversionsValue, 2)}</strong></td>
                  )}
                  {isComponentMetricActive('cos') && (
                    <td className={`numeric ${getCosClassName(totals.cos, selectedAccount?.id || 'global', moduleId)}`}>
                      <strong>{formatNumber(totals.cos, 1)}%</strong>
                    </td>
                  )}
                  {isComponentMetricActive('roas') && (() => {
                    const roas = totals.cost > 0 ? (totals.conversionsValue / totals.cost) * 100 : 0;
                    return (
                      <td className={`numeric ${getMetricObjectiveClassName('roas', roas, moduleId, selectedAccount?.id || 'global')}`}>
                        <strong>{roas >= 0 ? `${formatNumber(roas, 0)}%` : '-'}</strong>
                      </td>
                    );
                  })()}
                  {isComponentMetricActive('roi') && (() => {
                    const hasData = totals.cost > 0 && totals.conversionsValue > 0;
                    const roi = hasData ? (totals.conversionsValue - totals.cost) / totals.cost : 0;
                    return (
                      <td className={`numeric ${getMetricObjectiveClassName('roi', roi, moduleId, selectedAccount?.id || 'global')}`}>
                        <strong>{formatNumber(roi, 2)}</strong>
                      </td>
                    );
                  })()}
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination-container" style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '10px',
              marginTop: '20px',
              padding: '10px'
            }}>
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  background: currentPage === 1 ? '#f5f5f5' : 'white',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                }}
              >
                Précédent
              </button>

              <span style={{ padding: '0 10px', fontSize: '14px' }}>
                Page {currentPage} sur {totalPages}
              </span>

              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  background: currentPage === totalPages ? '#f5f5f5' : 'white',
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
                }}
              >
                Suivant
              </button>
            </div>
          )}
        </>
      )}

      {/* Modal de configuration des métriques */}
      {showMetricsConfig && (
        <MetricsConfigModal
          isOpen={showMetricsConfig}
          onClose={() => setShowMetricsConfig(false)}
          componentId="msads_adgroups"
          accountId={selectedAccount?.id || 'global'}
          title="Groupes d'annonces Microsoft Ads"
          hideApplyToAll={true}
        />
      )}
    </div>
  );
});

AdGroupsTable.displayName = 'AdGroupsTable';

export default AdGroupsTable;